import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CurrencyContext = createContext(null);

// Base prices are in USD
const USD_PRICES = {
  "sales-flow": { starter: 99, pro: 149, business: 199 },
  ops: { starter: 129, pro: 199, business: 249 },
  teams: { starter: 79, pro: 119, business: 149 },
  projects: { starter: 99, pro: 149, business: 199 },
  stockroom: { starter: 99, pro: 149, business: 199 },
  bookkeep: { starter: 99, pro: 149, business: 199 },
  support: { starter: 79, pro: 119, business: 149 },
  connect: { starter: 79, pro: 119, business: 149 },
  cerebrum: { starter: 49, pro: 79, business: 99 },
  bundle: { starter: 589, pro: 879, business: 1099 },
};

export function CurrencyProvider({ children }) {
  const [geo, setGeo] = useState({
    currency: "USD",
    symbol: "$",
    rate: 1.0,
    country: "",
    loaded: false,
  });

  useEffect(() => {
    axios
      .get(`${API}/api/geo`)
      .then((res) => {
        setGeo({
          currency: res.data.currency,
          symbol: res.data.symbol,
          rate: res.data.rate,
          country: res.data.country,
          loaded: true,
        });
      })
      .catch(() => {
        setGeo((g) => ({ ...g, loaded: true }));
      });
  }, []);

  return (
    <CurrencyContext.Provider value={geo}>{children}</CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

/**
 * Convert a USD base price to the visitor's local currency.
 * Returns a rounded whole number.
 */
export function useLocalPrice(usdAmount) {
  const { rate } = useCurrency();
  return Math.round(usdAmount * rate);
}

/**
 * Format a price with the visitor's currency symbol.
 * e.g. "R 1,832" or "$ 99" or "€ 92"
 */
export function useFormatPrice() {
  const { symbol } = useCurrency();
  return (usdAmount, rate) => {
    const local = Math.round(usdAmount * (rate || 1));
    return `${symbol} ${local.toLocaleString()}`;
  };
}

/**
 * Get module pricing converted to local currency.
 */
export function useModulePricing(moduleId) {
  const { rate } = useCurrency();
  const base = USD_PRICES[moduleId];
  if (!base) return null;
  return {
    starter: Math.round(base.starter * rate),
    pro: Math.round(base.pro * rate),
    business: Math.round(base.business * rate),
  };
}

/**
 * Get all module prices in local currency.
 */
export function useAllPricing() {
  const { rate } = useCurrency();
  const result = {};
  for (const [key, prices] of Object.entries(USD_PRICES)) {
    result[key] = {
      starter: Math.round(prices.starter * rate),
      pro: Math.round(prices.pro * rate),
      business: Math.round(prices.business * rate),
    };
  }
  return result;
}

export { USD_PRICES };
